import React, { useCallback, useState } from 'react';
import {
  Box,
  Text,
  Spinner,
  Button,
  Collapse,
  IconButton,
  HStack,
  VStack,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  useToast,
  FormControl,
  FormLabel,
  Badge,
  useColorModeValue,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { LuTrash2 } from 'react-icons/lu';
import { togglePushSubscription, deletePushSubscription } from './api/pushSubscriptions';
import { registerVapidSubscription } from './push/registerVapidSubscription';
import { usePushSubscriptionsQuery } from './query/usePushSubscriptions';
import { useQueryClient } from '@tanstack/react-query';
import { useOnboardingChecklist } from './onboarding/OnboardingChecklistProvider';

export default function DeliverToDevicesCard() {
  const [open, setOpen] = useState(true);
  const [loadingSubscribe, setLoadingSubscribe] = useState(false);

  // Modal state for device-name collection
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [deviceNameInput, setDeviceNameInput] = useState('');
  const [deviceTypeForPending, setDeviceTypeForPending] = useState('web');

  // Delete confirmation
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const cancelRef = React.useRef();

  // Per-item loading map: { [id]: { toggling: bool, deleting: bool } }
  const [itemLoading, setItemLoading] = useState({});

  const toast = useToast();
  const subscriptionsQuery = usePushSubscriptionsQuery();
  const { data: items = null, isLoading, isFetching, error } = subscriptionsQuery;
  const errorMessage = error ? (error.message || 'Failed to load devices') : null;
  const qc = useQueryClient();
  const { refresh: refreshChecklist } = useOnboardingChecklist();
  const invalidateSubscriptions = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['pushSubscriptions'] });
  }, [qc]);

  // Theme values (hooks must be called at top-level)
  const modalBg = useColorModeValue('bg.surface', 'bg.surface');
  const modalTextColor = 'text.primary';
  const boxBg = useColorModeValue('bg.subtle', 'bg.elevated');
  const smallTextColor = 'text.muted';
  const hoverDeleteBg = useColorModeValue('rgba(255,0,0,0.04)', 'rgba(255,0,0,0.06)');

  function inferDeviceType(ua) {
    const u = ua.toLowerCase();
    if (/iphone|ipad|ipod|ios/.test(u)) return 'ios';
    if (/android/.test(u)) return 'android';
    if (/mac os x|macintosh/.test(u)) return 'mac';
    if (/windows/.test(u)) return 'windows';
    if (/linux/.test(u)) return 'linux';
    if (/mobile/.test(u)) return 'mobile';
    return 'web';
  }

  async function subscribePushNotifications() {
    if (!('Notification' in window)) {
      toast({ title: 'This browser does not support notifications.', status: 'error' });
      return;
    }

    if (!('serviceWorker' in navigator)) {
      toast({ title: 'Service workers are not supported by this browser.', status: 'error' });
      return;
    }

    try {
      setLoadingSubscribe(true);
      let permission = Notification.permission;
      if (permission !== 'granted') {
        permission = await Notification.requestPermission();
      }
      if (permission !== 'granted') {
        toast({ title: 'Notification permission not granted.', status: 'info' });
        return;
      }

      const deviceType = inferDeviceType(navigator.userAgent);
      setDeviceTypeForPending(deviceType);
      setDeviceNameInput('');
      setIsDeviceModalOpen(true);
    } catch (error) {
      console.error('Subscription failed:', error);
      toast({ title: 'Failed to subscribe to notifications.', status: 'error' });
    } finally {
      setLoadingSubscribe(false);
    }
  }

  async function confirmRegister() {
    try {
      setIsDeviceModalOpen(false);
      setLoadingSubscribe(true);
      await registerVapidSubscription({ deviceName: deviceNameInput || null, deviceType: deviceTypeForPending });
      await invalidateSubscriptions();
      refreshChecklist();
      toast({ title: 'Subscription saved', status: 'success' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Failed to register subscription', status: 'error' });
    } finally {
      setLoadingSubscribe(false);
      setDeviceNameInput('');
    }
  }

  async function handleToggle(s) {
    const id = s.id;
    setItemLoading(prev => ({ ...prev, [id]: { ...(prev[id] || {}), toggling: true } }));
    try {
      await togglePushSubscription(id, !s.disabled);
      await invalidateSubscriptions();
      refreshChecklist();
      toast({ title: s.disabled ? 'Enabled device' : 'Disabled device', status: 'success' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Toggle failed', status: 'error' });
    } finally {
      setItemLoading(prev => ({ ...prev, [id]: { ...(prev[id] || {}), toggling: false } }));
    }
  }

  function confirmDelete(s) {
    setDeleteTarget(s);
    setIsDeleteModalOpen(true);
  }

  async function doDelete() {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setItemLoading(prev => ({ ...prev, [id]: { ...(prev[id] || {}), deleting: true } }));
    try {
      setIsDeleteModalOpen(false);
      await deletePushSubscription(id);
      await invalidateSubscriptions();
      refreshChecklist();
      toast({ title: 'Deleted subscription', status: 'success' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Delete failed', status: 'error' });
    } finally {
      setItemLoading(prev => ({ ...prev, [id]: { ...(prev[id] || {}), deleting: false } }));
      setDeleteTarget(null);
    }
  }

  return (
    <Box
      id="deliver-to-devices-card"
      borderWidth="1px"
      borderRadius="xl"
      p={0}
      mb={4}
      bg="bg.surface"
      borderColor="border.subtle"
    >
      <HStack p={3} justifyContent="space-between">
        <HStack>
          <IconButton
            aria-label={open ? 'Collapse' : 'Expand'}
            icon={open ? <FiChevronDown /> : <FiChevronRight />}
            size="sm"
            onClick={() => setOpen((v) => !v)}
          />
          <Text fontSize="lg" fontWeight="bold">Notifications</Text>
        </HStack>
        <Button
          id="register-device-button"
          variant="outline"
          colorScheme="blue"
          size="sm"
          onClick={subscribePushNotifications}
          isLoading={loadingSubscribe}
        >
          Register Device
        </Button>
      </HStack>

      <Collapse in={open} animateOpacity>
        <Box p={3} borderTopWidth="1px">
          <Text mt={0} opacity={0.8} color="text.muted">Manage notifications for your registered devices.</Text>
          {errorMessage && <Text color="red.500">Error: {errorMessage}</Text>}
          {items === null && !errorMessage && (isLoading || isFetching) && <Spinner />}

          {items && items.length === 0 && (
            <Box>
              <Text>No devices registered yet.</Text>
              <Text fontSize="sm" color="text.muted">Use the button above to register this device for notifications.</Text>
            </Box>
          )}

          {items && items.length > 0 && (
            <VStack spacing={2} mt={2} align="stretch">
              {items.map((s) => (
                <HStack key={s.id} display="flex" justifyContent="space-between" p={2} borderWidth="1px" borderRadius="md">
                  <VStack align="stretch" spacing={0} flex={1}>
                    <Text fontWeight="bold">{s.deviceName || (s.auth ? s.auth.substring(0, 8) : 'Unknown')}</Text>
                    <HStack>
                      <Text fontSize="sm" color="text.muted">{s.deviceType || 'web'}</Text>
                      <Badge colorScheme={s.disabled ? 'gray' : 'green'}>{s.disabled ? 'Disabled' : 'Enabled'}</Badge>
                    </HStack>
                  </VStack>

                  {/* <Text fontSize="xs" color="gray.500">{s.createdAt ? new Date(s.createdAt).toLocaleString() : ''}</Text> */}

                  <HStack>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor={`toggle-${s.id}`} mb="0" fontSize="sm">{s.disabled ? 'Off' : 'On'}</FormLabel>
                      <Switch
                        id={`toggle-${s.id}`}
                        isChecked={!s.disabled}
                        isDisabled={itemLoading[s.id]?.toggling}
                        onChange={() => handleToggle(s)}
                      />
                    </FormControl>

                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<LuTrash2 />}
                      color="red.400"
                      _hover={{ bg: hoverDeleteBg }}
                      isLoading={itemLoading[s.id]?.deleting}
                      onClick={() => confirmDelete(s)}
                    >
                      Delete
                    </Button>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          )}

          <Text mt={3} fontSize="xs" color="text.muted">Turning a device off stops delivery, it does not pause the task.</Text>
        </Box>
      </Collapse>

      {/* Device name modal (themed darker like schedule delete) */}
      <Modal isOpen={isDeviceModalOpen} onClose={() => setIsDeviceModalOpen(false)} isCentered>
        <ModalOverlay />
  <ModalContent
    bg={modalBg}
    color={modalTextColor}
    boxShadow={useColorModeValue('0 12px 32px rgba(31,24,13,0.12)', 'dark-lg')}
    borderRadius="xl"
    borderWidth="1px"
    borderColor="border.subtle"
  >
          <ModalHeader>Name this device</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={2}>Give this device a friendly name so you can recognize it later (optional).</Text>
            <Input placeholder="e.g. My iPhone" value={deviceNameInput} onChange={(e) => setDeviceNameInput(e.target.value)} />
            <Text fontSize="xs" color={smallTextColor} mt={2}>Detected type: {deviceTypeForPending}</Text>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} variant="outline" onClick={() => { setIsDeviceModalOpen(false); setDeviceNameInput(''); }}>Cancel</Button>
            <Button variant="outline" colorScheme="blue" onClick={confirmRegister}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirm (use AlertDialog like ScheduleItem for better theming) */}
      <AlertDialog isOpen={isDeleteModalOpen} leastDestructiveRef={cancelRef} onClose={() => setIsDeleteModalOpen(false)}>
          <AlertDialogOverlay>
          <AlertDialogContent bg={modalBg} color={modalTextColor}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete subscription</AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={3}>You are about to delete the following subscription:</Text>
              <Box borderWidth="1px" borderRadius="md" p={3} mb={3} bg={boxBg}>
                <Text fontWeight="bold" fontSize="md" mb={2}>{deleteTarget?.deviceName || deleteTarget?.auth?.substring(0, 8)}</Text>
                <Text fontSize="sm" color={smallTextColor}>{deleteTarget?.deviceType || ''}</Text>
              </Box>
              <Text color={smallTextColor}>Deleting this will stop all future notifications for this device and remove it from your list.</Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
              <Button colorScheme="red" onClick={doDelete} ml={3} isLoading={deleteTarget ? itemLoading[deleteTarget.id]?.deleting : false} leftIcon={<LuTrash2 />}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
